import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Shield, FileText, Download } from 'lucide-react';
import { PublicQrPageHeader } from '../features/electrical-systems/shared/PublicQrPageHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtDate = (d) => {
  if (!d) return '-';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
  return d;
};

const DETAIL_FIELDS = [
  { key: 'tag', label: 'Tag' },
  { key: 'location', label: 'Location' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'model', label: 'Model' },
  { key: 'serialNumber', label: 'Serial Number' },
  { key: 'voltage', label: 'Voltage' },
  { key: 'phase', label: 'Phase' },
  { key: 'hp', label: 'HP' },
  { key: 'rpm', label: 'RPM' },
  { key: 'installDate', label: 'Install Date', type: 'date' },
  { key: 'lastMaintenanceDate', label: 'Last Maintenance', type: 'date' },
];

export default function PublicEquipmentPage() {
  const { qrToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/public/review/electrical-equipment/${qrToken}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Equipment not found' : 'Failed to load equipment');
      } finally { setLoading(false); }
    };
    load();
  }, [qrToken]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-slate-300" /><span className="text-sm font-semibold text-slate-700">Specard Inc.</span>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-700">{error}</h2>
          <p className="text-sm text-slate-400 mt-1">The QR code may be invalid or the equipment has been removed.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-equipment-page">
      <PublicQrPageHeader
        title="Equipment Card"
        tagLabel="Equipment Tag"
        tagValue={data.tag}
        qrToken={data.qrToken}
        data={data}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-2">
        {/* Equipment Details */}
        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="public-equipment-details">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Equipment Details</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            {DETAIL_FIELDS.map(f => {
              const raw = data[f.key];
              const val = f.type === 'date' ? fmtDate(raw) : (raw == null || raw === '' ? '-' : String(raw));
              return (
                <div key={f.key} data-testid={`public-equip-${f.key}`}>
                  <dt className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{f.label}</dt>
                  <dd className="text-sm text-slate-900 font-medium mt-0.5">{val}</dd>
                </div>
              );
            })}
          </div>
          {data.notes && (
            <div className="mt-3 pt-3 border-t">
              <dt className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Notes</dt>
              <dd className="text-sm text-slate-900 mt-0.5 whitespace-pre-wrap">{data.notes}</dd>
            </div>
          )}
        </div>

        {/* Submittals */}
        {data.submittals?.length > 0 && (
          <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="public-equipment-submittals">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Submittals ({data.submittals.length})</h2>
            <div className="space-y-2">
              {data.submittals.map(f => (
                <div key={f.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-3 py-2">
                  <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="truncate flex-1">{f.name}</span>
                  {f.dataType && <span className="text-xs bg-slate-200 text-slate-600 rounded px-1.5 py-0.5 shrink-0">{f.dataType}</span>}
                  <span className="text-xs text-slate-400 shrink-0">{((f.size || 0) / 1024).toFixed(1)} KB</span>
                  <a href={`${API}/api/electrical-equipment/${data.projectId}/${data.id}/submittals/${f.id}/download`} className="text-orange-500 hover:text-orange-700 shrink-0">
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-4 mb-6">This is a read-only equipment card generated by Specard</p>
      </div>
    </div>
  );
}
